import React, { useEffect, useState } from 'react'

interface SuccessAnimationProps {
  isVisible: boolean
  message?: string
  onComplete?: () => void
  duration?: number
  size?: 'sm' | 'md' | 'lg'
}

const SuccessAnimation = React.memo<SuccessAnimationProps>(({
  isVisible,
  message = 'Success!',
  onComplete,
  duration = 2000,
  size = 'md'
}) => {
  const [showAnimation, setShowAnimation] = useState(false)
  const [showMessage, setShowMessage] = useState(false)

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16'
  }

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  }

  useEffect(() => {
    if (isVisible) {
      setShowAnimation(true)
      
      // Show message after checkmark animation
      const messageTimer = setTimeout(() => {
        setShowMessage(true)
      }, 600)

      // Complete animation
      const completeTimer = setTimeout(() => {
        setShowAnimation(false)
        setShowMessage(false)
        onComplete?.()
      }, duration)

      return () => {
        clearTimeout(messageTimer)
        clearTimeout(completeTimer)
      }
    }
  }, [isVisible, duration, onComplete])

  if (!isVisible && !showAnimation) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-25">
      <div className="bg-white rounded-lg p-8 shadow-xl max-w-sm mx-4 text-center">
        {/* Animated Checkmark */}
        <div className={`mx-auto mb-4 ${sizeClasses[size]} relative`}>
          <svg
            className={`
              ${sizeClasses[size]} text-green-500 transform transition-all duration-500 ease-out
              ${showAnimation ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}
            `}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              className="animate-pulse"
              strokeWidth="2"
              fill="currentColor"
              fillOpacity="0.1"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="3"
              d="M9 12l2 2 4-4"
              className="animate-[draw_0.6s_ease-out_forwards]"
              style={{
                strokeDasharray: '20',
                strokeDashoffset: showAnimation ? '0' : '20',
                transition: 'stroke-dashoffset 0.6s ease-out'
              }}
            />
          </svg>
        </div>

        {/* Success Message */}
        <div
          className={`
            transition-all duration-300 ease-out
            ${showMessage ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
          `}
        >
          <h3 className={`font-semibold text-gray-900 ${textSizeClasses[size]}`}>
            {message}
          </h3>
        </div>
      </div>
    </div>
  )
})

SuccessAnimation.displayName = 'SuccessAnimation'

export default SuccessAnimation